export interface MotorPythonResponse {
  status: string;
  conteo?: number;
  area_ha?: number;
  area_source?: string;
  hileras?: number;
  longitud_m?: number;
  longitud_promedio_hilera_m?: number;
  plantas_promedio_hilera?: number;
  surcos_estimados?: number;
  angulo_hilera_deg?: number;
  hileras_detalle?: Array<{ hilera: number; plantas: number; longitud_m: number; calidad_hilera?: string; ratio_vs_mediana?: number; distancia_siembra_cm?: number }>;
  alerta_desbalance?: boolean;
  distancia_promedio_siembra_cm?: number;
  densidad?: number;
  kpi?: number;
  detect_mode?: string;
  puntos?: Array<{ x?: number; y?: number; lon?: number; lat?: number }>;
  coordenadas?: Array<{ x?: number; y?: number; lon?: number; lat?: number }>;
  mensaje?: string;
  resultado_json?: string;
  resultado_imagen?: string;
  resultado_reporte?: string;
  resultado_json_url?: string;
  resultado_imagen_url?: string;
  resultado_reporte_url?: string;
}

export interface MotorPythonBatchBlock {
  bloque_id?: string;
  bloque_num?: number;
  geometry?: { type?: string; coordinates?: any };
  geometry_export?: { type?: string; coordinates?: any };
  area_ha?: number;
  area_source?: string;
  conteo?: number;
  hileras?: number;
  longitud_m?: number;
  longitud_promedio_hilera_m?: number;
  plantas_promedio_hilera?: number;
  plantas_promedio_cama?: number;
  surcos_estimados?: number;
  camas_estimadas?: number;
  camas_equivalentes?: number;
  hileras_por_cama?: number;
  hileras_sobrantes?: number;
  angulo_hilera_deg?: number;
  hileras_detalle?: Array<{ hilera: number; plantas: number; longitud_m: number; calidad_hilera?: string; ratio_vs_mediana?: number; distancia_siembra_cm?: number }>;
  alerta_desbalance?: boolean;
  distancia_promedio_siembra_cm?: number;
  densidad?: number;
  kpi?: number;
  detect_mode?: string;
  debug_camas_url?: string;
  coord_space?: 'wgs84' | 'wgs84_ref' | 'pixel' | 'mixed' | 'unknown' | string;
  error?: string;
}

export interface MotorPythonBatchResponse {
  status: string;
  mensaje?: string;
  warning?: string;
  coord_space?: 'wgs84' | 'wgs84_ref' | 'pixel' | 'mixed' | string;
  bloques?: MotorPythonBatchBlock[];
  geojson_poligonos?: Array<{ bloque_num?: number; geometry?: { type?: string; coordinates?: any }; coord_space?: string }>;
  resultado_json?: string;
  resultado_reporte?: string;
  resultado_json_url?: string;
  resultado_reporte_url?: string;
}

export interface AutoDetectarBloquesResponse {
  status?: string;
  mensaje?: string;
  bloques?: Array<{ bloque_num: number; puntos_norm: Array<[number, number]> }>;
}

function resolvePythonBaseUrl(): string {
  const explicit = String((process.env.REACT_APP_PY_API_BASE as string) || '').trim();
  if (explicit) return explicit.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://127.0.0.1:3001/api/python';
    }
  }
  return '/api/python';
}

function absolutizeArtifactUrl(endpointUrl: string, rawUrl?: string): string | undefined {
  if (!rawUrl) return undefined;
  if (/^https?:\/\//i.test(rawUrl)) {
    try {
      const parsed = new URL(rawUrl);
      if (parsed.pathname.startsWith('/resultados/')) {
        return `${parsed.origin}/api/python${parsed.pathname}${parsed.search || ''}`;
      }
    } catch {
      return rawUrl;
    }
    return rawUrl;
  }
  try {
    const origin = new URL(endpointUrl).origin;
    if (rawUrl.startsWith('/resultados/')) {
      return `${origin}/api/python${rawUrl}`;
    }
    if (rawUrl.startsWith('/')) return `${origin}${rawUrl}`;
    return `${origin}/${rawUrl}`;
  } catch {
    return rawUrl;
  }
}

async function postDetectWithFile(
  url: string,
  loteId: string,
  params: { minArea: number; maxArea: number },
  geojson: unknown,
  rasterFile: File
): Promise<MotorPythonResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const formData = new FormData();
    formData.append('file', rasterFile);
    formData.append('lote_id', loteId);
    formData.append('min_area', String(params.minArea));
    formData.append('max_area', String(params.maxArea));
    formData.append('geojson', JSON.stringify(geojson));

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: formData,
      signal: controller.signal,
    });

    if (!response.ok) {
      const bodyText = await response.text().catch(() => '');
      throw new Error(
        `Motor respondió ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`
      );
    }

    const payload = (await response.json()) as MotorPythonResponse;
    return {
      ...payload,
      resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
      resultado_imagen_url: absolutizeArtifactUrl(url, payload.resultado_imagen_url),
      resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
    };
  } finally {
    clearTimeout(timeout);
  }
}

async function postDetect(
  url: string,
  loteId: string,
  params: { minArea: number; maxArea: number },
  geojson: unknown
): Promise<MotorPythonResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lote_id: loteId,
        min_area: params.minArea,
        max_area: params.maxArea,
        geojson,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const bodyText = await response.text().catch(() => '');
      throw new Error(
        `Motor respondió ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`
      );
    }

    const payload = (await response.json()) as MotorPythonResponse;
    return {
      ...payload,
      resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
      resultado_imagen_url: absolutizeArtifactUrl(url, payload.resultado_imagen_url),
      resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
    };
  } finally {
    clearTimeout(timeout);
  }
}

export const invocarMotorPython = async (
  loteId: string,
  params: { minArea: number; maxArea: number },
  geojson: unknown,
  rasterFile?: File | null
): Promise<MotorPythonResponse> => {
  const baseUrl = resolvePythonBaseUrl();
  const normalized = baseUrl.replace(/\/$/, '');
  const urls = rasterFile
    ? [`${normalized}/api/v1/detectar-archivo`, `${normalized}/detectar-archivo`]
    : [`${normalized}/api/v1/detectar`, `${normalized}/detectar`];

  let lastError: unknown = null;
  for (const url of urls) {
    try {
      if (rasterFile) {
        return await postDetectWithFile(url, loteId, params, geojson, rasterFile);
      }
      return await postDetect(url, loteId, params, geojson);
    } catch (error) {
      lastError = error;
      console.warn(`Intento fallido contra ${url}:`, error);
    }
  }
  console.error('No se pudo alcanzar el motor Python en ningun endpoint:', lastError);
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const invocarMotorPythonBatchArchivo = async (
  loteId: string,
  params: { minArea: number; maxArea: number },
  bloques: Array<{ bloque_id: string; bloque_num: number; area_ha?: number; geojson: unknown }>,
  rasterFile: File
): Promise<MotorPythonBatchResponse> => {
  const baseUrl = resolvePythonBaseUrl();
  const normalized = baseUrl.replace(/\/$/, '');
  const urls = [`${normalized}/api/v1/detectar-bloques-archivo`, `${normalized}/detectar-bloques-archivo`];
  let lastError: unknown = null;

  for (const url of urls) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const formData = new FormData();
      formData.append('file', rasterFile);
      formData.append('lote_id', loteId);
      formData.append('min_area', String(params.minArea));
      formData.append('max_area', String(params.maxArea));
      formData.append('bloques', JSON.stringify(bloques));

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        throw new Error(
          `Motor respondió ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`
        );
      }

      const payload = (await response.json()) as MotorPythonBatchResponse;
      return {
        ...payload,
        bloques: (payload.bloques || []).map((b) => ({
          ...b,
          debug_camas_url: absolutizeArtifactUrl(url, b.debug_camas_url),
        })),
        resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
        resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
      };
    } catch (error) {
      lastError = error;
      console.warn(`Intento batch fallido contra ${url}:`, error);
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const invocarMotorPythonBatchArchivoV3 = async (
  loteId: string,
  params: { minArea: number; maxArea: number },
  bloques: Array<{ bloque_id: string; bloque_num: number; area_ha?: number; geojson: unknown }>,
  rasterFile: File
): Promise<MotorPythonBatchResponse> => {
  const baseUrl = resolvePythonBaseUrl();
  const normalized = baseUrl.replace(/\/$/, '');
  const urls = [`${normalized}/api/v1/detectar-bloques-v3-archivo`, `${normalized}/detectar-bloques-v3-archivo`];
  let lastError: unknown = null;

  for (const url of urls) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const formData = new FormData();
      formData.append('file', rasterFile);
      formData.append('lote_id', loteId);
      formData.append('min_area', String(params.minArea));
      formData.append('max_area', String(params.maxArea));
      formData.append('bloques', JSON.stringify(bloques));

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        throw new Error(
          `Motor respondiÃ³ ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`
        );
      }

      const payload = (await response.json()) as MotorPythonBatchResponse;
      return {
        ...payload,
        resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
        resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
      };
    } catch (error) {
      lastError = error;
      console.warn(`Intento batch V3 fallido contra ${url}:`, error);
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const invocarMotorPythonArchivoPoligono = async (
  params: { minArea: number; maxArea: number },
  puntosNormalizados: Array<[number, number]>,
  rasterFile: File,
  loteId = 'upload',
  options?: { areaMode?: 'auto' | 'fixed_gsd'; gsdM?: number; detectMode?: 'adaptive' | 'conteo1' | 'conteo5' | 'conteo5_simple' | 'conteo5_camas' }
): Promise<MotorPythonResponse> => {
  const baseUrl = resolvePythonBaseUrl();
  const normalized = baseUrl.replace(/\/$/, '');
  const urls = [`${normalized}/api/v1/detectar-archivo-poligono`, `${normalized}/detectar-archivo-poligono`];
  let lastError: unknown = null;

  for (const url of urls) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const formData = new FormData();
      formData.append('file', rasterFile);
      formData.append('min_area', String(params.minArea));
      formData.append('max_area', String(params.maxArea));
      formData.append('puntos', JSON.stringify(puntosNormalizados));
      formData.append('lote_id', loteId);
      formData.append('area_mode', options?.areaMode || 'auto');
      formData.append('gsd_m', String(options?.gsdM ?? 0.05));
      formData.append('detect_mode', options?.detectMode || 'adaptive');

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        throw new Error(
          `Motor respondio ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`
        );
      }

      const payload = (await response.json()) as MotorPythonResponse;
      return {
        ...payload,
        resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
        resultado_imagen_url: absolutizeArtifactUrl(url, payload.resultado_imagen_url),
        resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
      };
    } catch (error) {
      lastError = error;
      console.warn(`Intento poligono fallido contra ${url}:`, error);
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const invocarMotorPythonBloquesPoligonoArchivo = async (
  params: { minArea: number; maxArea: number },
  bloquesPuntos: Array<{ bloque_num: number; puntos: Array<[number, number]> }>,
  rasterFile: File,
  rasterRefFile?: File | null,
  loteId = 'upload',
  options?: { areaMode?: 'auto' | 'fixed_gsd'; gsdM?: number; detectMode?: 'adaptive' | 'conteo1' | 'conteo5' | 'conteo5_simple' | 'conteo5_camas' }
): Promise<MotorPythonBatchResponse> => {
  const baseUrl = resolvePythonBaseUrl();
  const normalized = baseUrl.replace(/\/$/, '');
  const urls = [`${normalized}/api/v1/detectar-bloques-poligono-archivo`, `${normalized}/detectar-bloques-poligono-archivo`];
  let lastError: unknown = null;

  for (const url of urls) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const formData = new FormData();
      formData.append('file', rasterFile);
      formData.append('min_area', String(params.minArea));
      formData.append('max_area', String(params.maxArea));
      formData.append('bloques_puntos', JSON.stringify(bloquesPuntos));
      formData.append('lote_id', loteId);
      formData.append('area_mode', options?.areaMode || 'auto');
      formData.append('gsd_m', String(options?.gsdM ?? 0.05));
      formData.append('detect_mode', options?.detectMode || 'adaptive');
      if (rasterRefFile) {
        formData.append('raster_ref_file', rasterRefFile);
      }

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => '');
        throw new Error(`Motor respondio ${response.status} en ${url}${bodyText ? ` - ${bodyText.slice(0, 220)}` : ''}`);
      }

      const payload = (await response.json()) as MotorPythonBatchResponse;
      return {
        ...payload,
        resultado_json_url: absolutizeArtifactUrl(url, payload.resultado_json_url),
        resultado_reporte_url: absolutizeArtifactUrl(url, payload.resultado_reporte_url),
      };
    } catch (error) {
      lastError = error;
      console.warn(`Intento bloques poligono fallido contra ${url}:`, error);
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

export const invocarAutoDetectarBloques = async (
  _rasterFile: File,
  _geojson: unknown,
  _loteId = 'upload',
  _gsdM = 0.05,
  _minGapM = 1.5
): Promise<AutoDetectarBloquesResponse> => {
  throw new Error('Auto-deteccion de bloques no disponible en el motor actual. Dibuje los bloques manualmente.');
};
